import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Receipt, ShieldCheck, Wrench } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { minorFromInput } from '@/components/MoneyInput';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const REGISTER_TAB = '/assets?tab=register';
const PATIENT_MODULES = ['lab', 'radiology', 'ipd', 'ot', 'er', 'opd'] as const;

function initialForm(treatsPatients: boolean) {
  return {
    code: '',
    name: '',
    category: treatsPatients ? 'biomedical' : 'other',
    manufacturer: '',
    serialNo: '',
    location: '',
    purchasedAt: '',
    purchaseCost: '0',
    depreciationPct: 15,
    warrantyEndsAt: '',
    amcVendor: '',
    amcEndsAt: '',
  };
}

function isoMidnight(date: string): string | null {
  return date ? new Date(`${date}T00:00:00.000Z`).toISOString() : null;
}

export function AddAssetPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hasModule } = useAuth();
  const returnTo = safeReturnTo(params.get('returnTo'), REGISTER_TAB);
  const [initial] = useState(() => initialForm(PATIENT_MODULES.some((m) => hasModule(m))));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      post('/assets', {
        code: form.code,
        name: form.name,
        category: form.category,
        manufacturer: form.manufacturer || undefined,
        serialNo: form.serialNo || undefined,
        location: form.location || undefined,
        purchasedAt: isoMidnight(form.purchasedAt),
        purchaseCostMinor: minorFromInput(form.purchaseCost),
        depreciationPct: form.depreciationPct,
        warrantyEndsAt: isoMidnight(form.warrantyEndsAt),
        amcVendor: form.amcVendor || undefined,
        amcEndsAt: isoMidnight(form.amcEndsAt),
      }),
    onSuccess: () => {
      toast.success('Asset added');
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const valid = form.code.length >= 2 && form.name.length >= 2;
  const dirty = !create.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title="Add an asset"
      description="Warranty and AMC dates decide what a breakdown costs"
      icon={Wrench}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setError(null);
        create.mutate();
      }}
    >
      <FormSection title={tr('admin', 'identity')} icon={Wrench}>
        <Field label="Code" required>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
        </Field>
        <Field label="Name" required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Category">
          <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {['biomedical', 'it', 'furniture', 'vehicle', 'building', 'other'].map((c) => (
              <option key={c} value={c}>
                {humanize(c)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Location">
          <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        </Field>
        <Field label="Manufacturer">
          <Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} />
        </Field>
        <Field label="Serial number">
          <Input value={form.serialNo} onChange={(e) => setForm({ ...form, serialNo: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('pharmacy', 'cost')} icon={Receipt} columns={3}>
        <Field label="Purchased">
          <DatePicker value={form.purchasedAt} onChange={(value) => setForm({ ...form, purchasedAt: value })} />
        </Field>
        <Field label="Purchase cost">
          <Input type="text" dir="ltr" inputMode="decimal" value={form.purchaseCost} onChange={(e) => setForm({ ...form, purchaseCost: e.target.value })} />
        </Field>
        <Field label="Depreciation % per year">
          <Input type="number" min={0} max={100} value={form.depreciationPct} onChange={(e) => setForm({ ...form, depreciationPct: Number(e.target.value) })} />
        </Field>
      </FormSection>

      <FormSection title={tr('pharmacy', 'cover')} icon={ShieldCheck} columns={3}>
        <Field label="Warranty ends">
          <DatePicker value={form.warrantyEndsAt} onChange={(value) => setForm({ ...form, warrantyEndsAt: value })} />
        </Field>
        <Field label="AMC vendor">
          <Input value={form.amcVendor} onChange={(e) => setForm({ ...form, amcVendor: e.target.value })} />
        </Field>
        <Field label="AMC ends">
          <DatePicker value={form.amcEndsAt} onChange={(value) => setForm({ ...form, amcEndsAt: value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Add" loading={create.isPending} incomplete={!valid} cancelTo={returnTo} />
    </FormScreen>
  );
}
